import { NotificationIndicatorController } from './controllers/notification-indicator-controller';
import { NotificationGroupUpdate } from './interfaces/notification-group-update';
import { ActivityFeedNotificationItem } from './models/activity-feed-notification-item';
import { AssignmentNotificationItemGroup } from './models/assignment-notification-item-group';
import { AssignmentTemplateNotificationItem } from './models/assignment-template-notification-item';
import { CommunicationNotificationItem } from './models/communication-notification-item';
import { CommunicationNotificationItemGroup } from './models/communication-notification-item-group';
import { NotificationItemGroup } from './models/notification-item-group';
import { PdsNotificationEvent } from './interfaces/pds-notification-event';
import { BasicThreadContext } from './interfaces/basic-thread-context';
import { PdsWidget } from './pds-widget';
import { Subscription } from 'rxjs';
import { ChatThreadRI } from 'common-utils/rest-interfaces';

export class PdsNotificationIndicator extends PdsWidget {
  is = 'pds-notification-indicator';

  controller: NotificationIndicatorController;

  dataDisableViewAllButton: string;
  dataEnableChatTab: string;
  dataEnableAssignmentsTab: string;

  properties = {
    dataDisableViewAllButton: {
      type: String,
      value: 'false'
    },
    dataEnableChatTab: {
      type: String,
      value: 'false'
    },
    dataEnableAssignmentsTab: {
      type: String,
      value: 'false'
    }
  };

  isNotificationDisplayShowing: boolean;

  //Data we show in the template
  totalUnreadNotificationCount: number | null;

  currentTabUnreadCount: number | null;
  notificationGroups: Array<NotificationItemGroup> | null;
  unreadNotificationCounts: { [notificationType: string]: number };
  currentNotificationType: string;

  //End data we show in the template

  subscriptions = [];
  notificationItemGroupSubscription: Subscription;

  get notificationIndicatorController(): NotificationIndicatorController {
    return this.controller;
  }

  set notificationIndicatorController(controller: NotificationIndicatorController) {
    this.controller = controller;
    this.notificationItemGroupSubscription = this.controller.notificationItemGroupStream.subscribe((notificationGroupUpdate: NotificationGroupUpdate) => {
      // the slice is to get a new copy of the array so that change detection will pick up on it
      this.notificationGroups = notificationGroupUpdate.notificationGroups.slice().sort(NotificationItemGroup.compare);
      this.totalUnreadNotificationCount = notificationGroupUpdate.totalUnreadNotificationCount;
      this.currentTabUnreadCount = notificationGroupUpdate.currentTabUnreadCount;
      this.unreadNotificationCounts = notificationGroupUpdate.notificationTypeUnreadCountMap;
      this.currentNotificationType = this.controller.currentNotificationType;
      this.redrawTrigger = !this.redrawTrigger;
    });
  }

  showTabs(): boolean {
    return this.dataEnableChatTab === 'true' || this.dataEnableAssignmentsTab === 'true';
  }

  ready(): void {
    super.ready();
    this.isNotificationDisplayShowing = false;
    this.unreadNotificationCounts = {};
  }

  detached(): void {
    super.detached();
    if (this.notificationItemGroupSubscription != null) {
      this.notificationItemGroupSubscription.unsubscribe();
    }
  }

  messageKeysUpdated(): void { }

  toggleNotificationDisplay(event?: Event): void {
    if (event != null) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (this.isNotificationDisplayShowing) {
      this.closeNotificationDisplay();
    } else {
      this.openNotificationDisplay();
    }
  }

  closeNotificationDisplay(): void {
    // set timeout is needed here because when the user clicks on the icon
    // they are also clicking on the body of the page, because useCapture is true (pds-overlay-dropdown.ts -> window.document.body.addEventListener('click', this.onBodyClick, true);)
    // this function was getting called before toggleHelpMenuDisplay
    // so this.isNotificationDisplayShowing was always equal to false, and the notifications dropdown
    // would not hide after clicking on the icon
    setTimeout(() => {
      this.isNotificationDisplayShowing = false;
    });
  }

  markAllRead(event?: Event): void {
    if (event != null) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.controller.markAllAsRead();
  }

  markAsRead(group: NotificationItemGroup): void {
    this.controller.markGroupAsRead(group);
  }

  openNotificationDisplay(): void {
    this.isNotificationDisplayShowing = true;
  }

  emitClickEvent(event: any): void {
    event.preventDefault();
    let itemGroup: NotificationItemGroup = event.model.notificationGroup; // polymer
    this._fireNotificationClickEvent(itemGroup);
  }

  handleAssignmentSubmissionClick(event: Event): void {
    event.preventDefault();
    let itemGroup: AssignmentNotificationItemGroup = (<any>event).model.notificationGroup;
    this._fireNotificationClickEvent(itemGroup, { sectionUids: itemGroup.getSectionUids() });
  }

  emitViewAllEvent(event: any): void {
    this.fire('pdsNotificationViewAll', null);
    this.markAllRead();
    this.isNotificationDisplayShowing = false;
  }

  setCurrentNotificationType(event: any): void {
    event.preventDefault();
    event.stopPropagation();
    this.controller.setCurrentNotificationType(event.currentTarget.dataset.notificationType);
    this.currentNotificationType = this.controller.currentNotificationType;
  }

  isTabBadgeActive(unreadNotificationCounts: { [notificationType: string]: number }, notificationType: string): boolean {
    let count: number = this.unreadNotificationCounts[notificationType];
    return count != null && count > 0;
  }

  getUnreadCountForTab(unreadNotificationCounts: { [notificationType: string]: number }, notificationType: string): number {
    return this.unreadNotificationCounts[notificationType] || 0;
  }

  handleMessageSentEvent(event: any): void {
    let itemGroup: CommunicationNotificationItemGroup = event.model.notificationGroup; // polymer
    let threadInfo = (<CommunicationNotificationItemGroup>itemGroup).getThreadContext();
    this.notificationIndicatorController.markReadByLookUpKeyAndFunction(
      itemGroup.lookUpKey,
      CommunicationNotificationItem.getIsPartOfThread(threadInfo.threadUid),
      true
    );
    this._fireNotificationClickEvent(itemGroup, {
      shouldMarkRead: true,
      shouldNavigate: false,
      threadType: threadInfo.threadType
    }, false);
  }

  handleChatNavigationEvent(event: CustomEvent): void {
    event.preventDefault();
    let chatThread: ChatThreadRI = event.detail;
    let notificationEvent: PdsNotificationEvent = {
      type: 'COMMUNICATION_NOTIFICATION', // default attriute for chat message navigation event
      shouldNavigate: true, // default attriute for chat message navigation event
      topicUid: chatThread.topicUid,
      threadOwnerUid: chatThread.ownerUid,
      threadType: chatThread.threadType
    };

    this.fire('pdsNotificationClick', notificationEvent);
  }

  getNotificationGroups(notificationGroups: Array<NotificationItemGroup>, redrawTrigger: boolean): Array<NotificationItemGroup> {
    return this.notificationGroups || [];
  }

  getAccessibilityTextForTab(redrawTrigger: boolean, notificationType: string): string {
    let count = this.getUnreadCountForTab(this.unreadNotificationCounts, notificationType);
    switch (notificationType) {
      case CommunicationNotificationItem.NOTIFICATION_TYPE:
        return this.pds.i18n.getText('pds|notifications|View xx Message Notifications', { count });
      case AssignmentTemplateNotificationItem.NOTIFICATION_TYPE:
        return this.pds.i18n.getText('pds|notifications|View xx Assignment Notifications', { count });
      case ActivityFeedNotificationItem.NOTIFICATION_TYPE:
      default:
        return this.pds.i18n.getText('pds|notifications|View xx Activity Feed Notifications', { count });
    }
  }

  getClearAllButtonText(redrawTrigger: boolean, notificationType: string): string {
    switch (notificationType) {
      case AssignmentTemplateNotificationItem.NOTIFICATION_TYPE:
        return this.pds.i18n.getText('pds|notification widget|Clear Assignment Notifications');
      default:
        return this.pds.i18n.getText('pds|notification widget|Clear All');
    }
  }

  private _fireNotificationClickEvent(itemGroup: NotificationItemGroup | AssignmentNotificationItemGroup, extraContext: any = {}, shouldCloseNotificationDisplay = true): void {
    let notificationEvent: PdsNotificationEvent = { ...JSON.parse(itemGroup.aggregationKey), ...extraContext };

    if (itemGroup.type === 'COMMUNICATION_NOTIFICATION' && itemGroup.getUnreadNotifications().length !== 0) {
      let threadContext: BasicThreadContext = (<CommunicationNotificationItemGroup>itemGroup).getThreadContext();
      notificationEvent = { ...notificationEvent, threadType: threadContext.threadType };
    }

    if (itemGroup.isMarkedReadOnClick) {
      this.markAsRead(itemGroup);
    }

    notificationEvent.type = (itemGroup instanceof AssignmentNotificationItemGroup) ? <string>itemGroup.subtype : itemGroup.type;
    this.fire('pdsNotificationClick', notificationEvent);
    this.isNotificationDisplayShowing = !shouldCloseNotificationDisplay;
  }
}
